import { Link, NavLink, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PriceFeePill from './PriceFeePill';
import BrandLogo from './BrandLogo';
import {
  FiAward,
  FiBarChart2,
  FiChevronDown,
  FiDatabase,
  FiGrid,
  FiLayers,
  FiMenu,
  FiMonitor,
  FiMoon,
  FiSun,
  FiUser,
  FiLogOut,
  FiX,
} from 'react-icons/fi';

const desktopNavClass = ({ isActive }) =>
  `nav-link main-nav-tab${isActive ? ' is-nav-active' : ''}`;

const mobileNavClass = ({ isActive }) =>
  `mobile-nav-link${isActive ? ' is-nav-active' : ''}`;

const getThemeMeta = (theme) => {
  if (theme === 'dark') {
    return { label: 'Dark', icon: <FiMoon /> };
  }
  if (theme === 'light') {
    return { label: 'Light', icon: <FiSun /> };
  }
  return { label: 'System', icon: <FiMonitor /> };
};

function getDesktopTabIndex(pathname) {
  if (pathname === '/') {
    return 0;
  }
  if (pathname.startsWith('/defi')) {
    return 1;
  }
  if (pathname.startsWith('/leaderboard')) {
    return 2;
  }
  if (pathname.startsWith('/developers') || pathname.startsWith('/addresses')) {
    return 3;
  }
  if (pathname.startsWith('/resources')) {
    return 4;
  }
  if (pathname.startsWith('/transactions') || pathname.startsWith('/tx')) {
    return 0;
  }
  return 0;
}

function Header({ theme, setTheme, activeTheme: resolvedTheme = 'light' }) {
  const location = useLocation();
  const { user, isAuthenticated, bootstrapping, logout } = useAuth();
  const [isBlockchainOpen, setIsBlockchainOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const themeChip = getThemeMeta(theme);

  const tabsWrapRef = useRef(null);
  const tabRefs = useRef([]);
  const [tabIndicator, setTabIndicator] = useState({ left: 0, width: 0, visible: false });

  const activeTabIndex = useMemo(() => getDesktopTabIndex(location.pathname), [location.pathname]);

  const updateTabIndicator = useCallback(() => {
    const wrap = tabsWrapRef.current;
    const el = tabRefs.current[activeTabIndex];
    if (!wrap || !el) {
      setTabIndicator((prev) => ({ ...prev, visible: false }));
      return;
    }
    const wrapRect = wrap.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    setTabIndicator({
      left: elRect.left - wrapRect.left + wrap.scrollLeft,
      width: elRect.width,
      visible: true,
    });
  }, [activeTabIndex]);

  useLayoutEffect(() => {
    updateTabIndicator();
  }, [updateTabIndicator, isBlockchainOpen]);

  useEffect(() => {
    window.addEventListener('resize', updateTabIndicator);
    return () => window.removeEventListener('resize', updateTabIndicator);
  }, [updateTabIndicator]);

  const setTabRef = (index) => (node) => {
    tabRefs.current[index] = node;
  };

  useEffect(() => {
    setIsProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeDropdowns = () => {
      setIsBlockchainOpen(false);
      setIsThemeOpen(false);
      setIsProfileOpen(false);
    };

    window.addEventListener('click', closeDropdowns);
    return () => window.removeEventListener('click', closeDropdowns);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="site-header">
      <div className="top-nav">
        <div className="left-cluster">
          <BrandLogo theme={resolvedTheme} linkTo="/" />
          <PriceFeePill className="price-fee-pill--header desktop-only" />
        </div>

        <div className="right-cluster">
          <nav className="main-nav desktop-only" aria-label="Primary">
            <div className="main-nav-tabs-wrap" ref={tabsWrapRef}>
              <div
                className="main-nav-tab-indicator"
                aria-hidden
                style={{
                  width: tabIndicator.width,
                  left: tabIndicator.left,
                  opacity: tabIndicator.visible ? 1 : 0,
                }}
              />
              <NavLink to="/" end className={desktopNavClass} ref={setTabRef(0)}>
                <FiBarChart2 />
                <span>Analytics</span>
              </NavLink>
              <NavLink to="/defi" className={desktopNavClass} ref={setTabRef(1)}>
                <FiLayers />
                <span>Defi</span>
              </NavLink>
              <NavLink to="/leaderboard" className={desktopNavClass} ref={setTabRef(2)}>
                <FiAward />
                <span>Leaderboard</span>
              </NavLink>

              <div
                ref={setTabRef(3)}
                className={`dropdown main-nav-tab-dropdown${isBlockchainOpen ? ' is-open' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsBlockchainOpen((prev) => !prev);
                  setIsThemeOpen(false);
                  setIsProfileOpen(false);
                }}
              >
                <button
                  type="button"
                  className={`nav-link main-nav-tab dropdown-trigger${activeTabIndex === 3 ? ' is-nav-active' : ''}`}
                >
                  <FiDatabase />
                  <span>Blockchain</span>
                  <FiChevronDown className="caret-icon" />
                </button>
                {isBlockchainOpen && (
                  <div className="dropdown-menu">
                    <a href="/resources">Explorer Overview</a>
                    <a href="/developers">Developer Center</a>
                    <a href="/addresses">Top Addresses</a>
                  </div>
                )}
              </div>
              <NavLink to="/resources" className={desktopNavClass} ref={setTabRef(4)}>
                <FiGrid />
                <span>Resources</span>
              </NavLink>
            </div>
          </nav>

          {!bootstrapping && !isAuthenticated ? (
            <Link to="/login" className="header-signin-btn desktop-only">
              Sign in
            </Link>
          ) : null}

          <div
            className={`dropdown desktop-only${isThemeOpen ? ' is-open' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              setIsThemeOpen((prev) => !prev);
              setIsBlockchainOpen(false);
              setIsProfileOpen(false);
            }}
          >
            <button type="button" className="theme-btn theme-chip">
              {themeChip.icon}
              <span>{themeChip.label}</span>
              <FiChevronDown className="caret-icon" />
            </button>
            {isThemeOpen && (
              <div className="dropdown-menu right">
                <button
                  type="button"
                  className={theme === 'light' ? 'is-active' : ''}
                  onClick={() => setTheme('light')}
                >
                  <FiSun />
                  <span>Light mode</span>
                </button>
                <button
                  type="button"
                  className={theme === 'dark' ? 'is-active' : ''}
                  onClick={() => setTheme('dark')}
                >
                  <FiMoon />
                  <span>Dark mode</span>
                </button>
                <button
                  type="button"
                  className={theme === 'system' ? 'is-active' : ''}
                  onClick={() => setTheme('system')}
                >
                  <FiMonitor />
                  <span>System theme</span>
                </button>
              </div>
            )}
          </div>

          {!bootstrapping && isAuthenticated ? (
            <div className="dropdown profile-menu-wrap desktop-only">
              <button
                type="button"
                className="profile-btn"
                aria-label="Account menu"
                aria-expanded={isProfileOpen}
                aria-haspopup="true"
                title={user?.email || 'Account'}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsProfileOpen((prev) => !prev);
                  setIsBlockchainOpen(false);
                  setIsThemeOpen(false);
                }}
              >
                <FiUser />
              </button>
              {isProfileOpen && (
                <div
                  className="dropdown-menu right profile-dropdown"
                  role="menu"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="profile-dropdown-user" role="presentation">
                    <span className="profile-dropdown-name">{user?.name || 'Account'}</span>
                    {user?.email ? (
                      <span className="profile-dropdown-email">{user.email}</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      logout();
                      setIsProfileOpen(false);
                    }}
                  >
                    <FiLogOut />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          ) : null}

          <button
            type="button"
            className="theme-btn icon-btn mobile-only"
            aria-label="Toggle theme"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <button
            type="button"
            className="theme-btn icon-btn mobile-only"
            aria-label="Open menu"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <FiMenu />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}>
          <aside className="mobile-menu-panel" onClick={(event) => event.stopPropagation()}>
            <div className="mobile-menu-head">
              <span>Menu</span>
              <button type="button" className="theme-btn icon-btn" onClick={() => setIsMobileMenuOpen(false)}>
                <FiX />
              </button>
            </div>

            <nav className="mobile-nav">
              <NavLink to="/" end className={mobileNavClass} onClick={() => setIsMobileMenuOpen(false)}>
                <FiBarChart2 />
                <span>Analytics</span>
              </NavLink>
              <NavLink to="/defi" className={mobileNavClass} onClick={() => setIsMobileMenuOpen(false)}>
                <FiLayers />
                <span>Defi</span>
              </NavLink>
              <NavLink to="/leaderboard" className={mobileNavClass} onClick={() => setIsMobileMenuOpen(false)}>
                <FiAward />
                <span>Leaderboard</span>
              </NavLink>
              <NavLink to="/resources" className={mobileNavClass} onClick={() => setIsMobileMenuOpen(false)}>
                <FiGrid />
                <span>Resources</span>
              </NavLink>
              <NavLink to="/resources" className={mobileNavClass} onClick={() => setIsMobileMenuOpen(false)}>
                <FiDatabase />
                <span>Explorer Overview</span>
              </NavLink>
              <NavLink to="/developers" className={mobileNavClass} onClick={() => setIsMobileMenuOpen(false)}>
                <FiDatabase />
                <span>Developer Center</span>
              </NavLink>
            </nav>

            <div className="mobile-auth-block">
              {!bootstrapping && isAuthenticated ? (
                <>
                  <span className="mobile-auth-user">{user?.name || user?.email}</span>
                  <button type="button" className="mobile-auth-btn" onClick={() => { logout(); setIsMobileMenuOpen(false); }}>
                    <FiLogOut />
                    <span>Sign out</span>
                  </button>
                </>
              ) : null}
              {!bootstrapping && !isAuthenticated ? (
                <Link
                  to="/login"
                  className="mobile-auth-btn is-primary mobile-auth-btn--full"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign in
                </Link>
              ) : null}
            </div>

            <div className="mobile-theme-switch" role="tablist" aria-label="Theme">
              <button
                type="button"
                role="tab"
                aria-selected={theme === 'light'}
                className={theme === 'light' ? 'is-active' : ''}
                onClick={() => setTheme('light')}
              >
                <FiSun />
                <span>Light</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={theme === 'dark'}
                className={theme === 'dark' ? 'is-active' : ''}
                onClick={() => setTheme('dark')}
              >
                <FiMoon />
                <span>Dark</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={theme === 'system'}
                className={theme === 'system' ? 'is-active' : ''}
                onClick={() => setTheme('system')}
              >
                <FiMonitor />
                <span>System</span>
              </button>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}

export default Header;
